import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Check, Plus, Package, Shield, Truck } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { saveOrderData, getOrderData, type SelectedActivity } from "@/lib/storage";
import { PhotoUpload } from "@/components/PhotoUpload";

interface Activity {
  id: number;
  name: string;
  description: string;
  image: string;
  category: string;
  price: string;
  detailedDescription: string;
  features: string[];
  contents: string[];
  duration?: string;
  difficulty?: string;
  benefits?: string[];
  tips?: string[];
  variants?: { id: string; label: string; value: string; image?: string }[];
}

const activities: Activity[] = [
  {
    id: 1,
    name: "Puzzle personnalisé",
    description: "Choisissez votre photo et envoyez un puzzle 1000 pièces à compléter ensemble !",
    detailedDescription: "Créez un moment unique en envoyant un puzzle personnalisé à votre grand-parent. Ce puzzle de 1000 pièces de haute qualité est fabriqué à partir de votre photo préférée. Une fois terminé, il devient un souvenir précieux à encadrer. L'activité stimule la mémoire, la concentration et procure un sentiment d'accomplissement.",
    image: "/lovable-uploads/ad7fac07-bb16-4ca9-98a9-704b804b2ab3.png",
    category: "Créatif",
    price: "Inclus dans l'abonnement",
    features: [
      "Photo personnalisée de votre choix",
      "1000 pièces de qualité premium",
      "Finition mate anti-reflets",
      "Boîte de présentation incluse"
    ],
    contents: [
      "1 puzzle personnalisé 1000 pièces",
      "1 boîte de rangement",
      "1 guide de montage",
      "1 carte personnalisée"
    ]
  },
  {
    id: 2,
    name: "Kit pour faire pousser ses propres plantes",
    description: "Choisissez votre kit de jardinage : herbes aromatiques, cactus ou bonzaï pour cultiver vos propres plantes !",
    detailedDescription: "Découvrez le plaisir du jardinage avec nos kits complets adaptés à tous les niveaux. Que vous choisissiez les herbes aromatiques pour parfumer votre cuisine, les cactus pour une décoration moderne ou les bonzaïs pour créer une ambiance zen, chaque kit contient tout le nécessaire pour réussir. Une activité relaxante qui stimule la patience et offre la satisfaction de voir grandir ses propres plantes.",
    image: "/lovable-uploads/3684dd22-b0f2-43ff-8143-89f55281de80.png",
    category: "Activité manuelle",
    price: "Inclus dans l'abonnement",
    duration: "2-4 semaines de croissance",
    difficulty: "Débutant à intermédiaire",
    variants: [
      { id: "jardin-1", label: "Kit herbe aromatique", value: "kit herbe aromatique", image: "/lovable-uploads/3684dd22-b0f2-43ff-8143-89f55281de80.png" },
      { id: "jardin-2", label: "Kit Cactus", value: "kit cactus", image: "/lovable-uploads/fc64caef-7b17-448c-9896-d4acf73d4d92.png" },
      { id: "jardin-3", label: "Kit Bonzaï", value: "kit bonzaï", image: "/lovable-uploads/502470bc-6927-49dc-84bc-c6b30cb4f384.png" }
    ],
    benefits: [
      "Activité relaxante et thérapeutique",
      "Développe la patience et la responsabilité", 
      "Apporte de la verdure dans le foyer",
      "Satisfaction de voir grandir ses plantes",
      "Apprentissage des techniques de jardinage"
    ],
    features: [
      "Kits complets avec tout le nécessaire",
      "Instructions détaillées et illustrées",
      "Graines de qualité sélectionnées",
      "Outils de jardinage professionnels",
      "Suivi de croissance personnalisé",
      "Conseils d'entretien inclus"
    ],
    contents: [
      "Pots adaptés au type de plante",
      "Manuel d'utilisation illustré",
      "Graines ou plants sélectionnés",
      "Outils de jardinage spécialisés",
      "Substrat et nutriments",
      "Pipette d'arrosage de précision",
      "Étiquettes de plantation",
      "Conseils d'entretien personnalisés"
    ],
    tips: [
      "Placez vos pots dans un endroit lumineux mais pas en plein soleil direct",
      "Respectez les quantités d'arrosage indiquées dans le manuel",
      "Soyez patient, la germination peut prendre 1-2 semaines",
      "Photographiez l'évolution de vos plantes pour garder un souvenir",
      "N'hésitez pas à partager votre expérience avec la famille"
    ]
  },
  {
    id: 5,
    name: "Kit peinture écologique et DIY",
    description: "Fabriquez votre propre peinture à partir de matériaux écologiques. Idéal pour expérimenter, bricoler et peintre d'une nouvelle manière !",
    detailedDescription: "Découvrez l'art de fabriquer sa propre peinture avec des matériaux naturels et écologiques. Ce kit éducatif et créatif permet d'explorer différentes techniques de peinture tout en respectant l'environnement. Perfect pour stimuler la créativité et apprendre de nouvelles compétences.",
    image: "/lovable-uploads/3847ac7e-ced9-4f12-bfeb-5d9586ab6c8a.png",
    category: "Activité manuelle",
    price: "Inclus dans l'abonnement",
    features: [
      "Matériaux 100% écologiques",
      "Techniques de fabrication naturelles",
      "Couleurs vibrantes et naturelles",
      "Guide créatif inclus"
    ],
    contents: [
      "Pigments naturels",
      "Liants écologiques",
      "Pinceaux de différentes tailles",
      "Support de peinture",
      "Guide créatif et recettes",
      "Tablier de protection"
    ]
  },
  {
    id: 7,
    name: "Kit de broderie pour débutant",
    description: "Kit débutant pour réaliser 4 magnifiques motifs floraux, avec instructions et vidéos incluses.",
    detailedDescription: "Découvrez l'art de la broderie avec ce kit complet spécialement conçu pour les débutants. Ce kit contient tout le nécessaire pour réaliser 4 magnifiques motifs floraux, chacun unique et soigneusement choisi pour offrir une progression naturelle dans l'apprentissage. La broderie est une activité méditative qui permet de se détendre tout en créant de belles décorations pour la maison. Chaque projet peut être encadré dans les cercles en bois fournis pour créer une décoration murale élégante. Les instructions détaillées et les vidéos explicatives rendent cette activité accessible à tous, même sans expérience préalable. C'est le cadeau parfait pour initier quelqu'un à cet art traditionnel ou pour offrir un moment de détente créative.",
    image: "/lovable-uploads/3be6d83f-b896-41ca-a229-f5b81666ea30.png",
    category: "Activité manuelle",
    price: "Inclus dans l'abonnement",
    duration: "2-3 heures par motif",
    difficulty: "Débutant",
    benefits: [
      "Activité relaxante et méditative",
      "Développe la dextérité et la patience",
      "Créations décoratives personnalisées",
      "Introduction à un art traditionnel",
      "Sentiment d'accomplissement garanti"
    ],
    features: [
      "4 motifs floraux différents progressifs",
      "Instructions étape par étape illustrées",
      "Vidéos explicatives en ligne",
      "Niveau débutant accessible",
      "Matériel de qualité professionnelle",
      "Résultat décoratif immédiat"
    ],
    contents: [
      "4 cercles à broder en bois naturel (15cm de diamètre)",
      "4 toiles en coton pré-imprimées avec motifs floraux",
      "Fils à broder DMC assortis (12 couleurs)",
      "3 aiguilles de broderie de tailles différentes", 
      "Manuel d'instructions illustré (32 pages)",
      "QR code d'accès aux vidéos tutoriels",
      "Pochette de rangement en tissu",
      "Ciseaux de broderie précision"
    ],
    tips: [
      "Commencez par le motif le plus simple pour vous familiariser",
      "Travaillez dans un endroit bien éclairé",
      "Prenez des pauses régulières pour éviter la fatigue",
      "N'hésitez pas à regarder les vidéos plusieurs fois",
      "La broderie est un art qui demande de la patience, savourez chaque point"
    ]
  },
  {
    id: 6,
    name: "Livre Raconte ton histoire",
    description: "Un journal unique avec 175 questions pour transmettre souvenirs et sagesse.",
    detailedDescription: "Offrez un trésor de souvenirs : un journal unique avec 175 questions soigneusement conçues pour permettre à vos grands-parents de raconter leur vie, partager leur sagesse et transmettre leur histoire aux générations futures. Ce livre devient un héritage précieux qui traverse les générations, créant un lien émotionnel fort entre les aînés et leur famille. Chaque question est pensée pour encourager les souvenirs et stimuler l'expression personnelle.",
    image: "/lovable-uploads/b2d82e88-70f2-4852-90e9-87f999a09e85.png",
    category: "Mémoriel",
    price: "Inclus dans l'abonnement",
    variants: [
      { id: "livre-1", label: "Pour Papi", value: "livre raconte ton histoire pour papi" },
      { id: "livre-2", label: "Pour Mamie", value: "livre raconte ton histoire pour mamie" }
    ],
    features: [
      "175 questions soigneusement sélectionnées",
      "Format adapté aux personnes âgées",
      "Papier de qualité supérieure",
      "Reliure solide et durable",
      "Questions organisées par thèmes",
      "Espace généreux pour les réponses"
    ],
    contents: [
      "1 livre 'Raconte ton histoire'",
      "175 questions réparties en thèmes",
      "Pages lignées pour l'écriture",
      "Boîte de présentation"
    ]
  },
  {
    id: 8,
    name: "Écouter des souvenirs musicaux",
    description: "Offrez un voyage musical unique : une playlist personnalisable des plus belles chansons du passé, gravée sur CD, vinyle ou clé USB selon l'équipement de vos grands-parents, pour raviver leurs souvenirs, faire renaître leurs émotions et danser toute la journée.",
    detailedDescription: "Redécouvrez la magie de la musique avec notre service de souvenirs musicaux personnalisés. Créez une compilation unique des chansons qui ont marqué la vie de votre grand-parent, des tubes de leur jeunesse aux mélodies qui les font encore danser aujourd'hui. Chaque playlist est soigneusement gravée sur le support de votre choix - CD pour la facilité d'utilisation, vinyle pour l'authenticité nostalgique, ou clé USB pour la modernité. Cette activité permet de revivre des moments précieux, de partager des souvenirs en famille et de transmettre l'héritage musical aux nouvelles générations. La musique a ce pouvoir unique de réveiller des émotions enfouies et de créer des liens intergénérationnels magiques.",
    image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png",
    category: "Loisir",
    price: "Inclus dans l'abonnement",
    duration: "Playlist de 60-90 minutes",
    difficulty: "Aucune difficulté",
    variants: [
      { id: "musique-1", label: "CD", value: "cd", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" },
      { id: "musique-2", label: "Vinyle", value: "vinyle", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" },
      { id: "musique-3", label: "Clé USB", value: "usb", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" }
    ],
    benefits: [
      "Stimule la mémoire et les souvenirs",
      "Favorise le bien-être émotionnel",
      "Renforce les liens familiaux",
      "Encourage l'activité physique (danse)",
      "Transmission du patrimoine musical"
    ],
    features: [
      "Playlist 100% personnalisée",
      "Choix du format (CD/Vinyle/USB)",
      "Qualité audio optimale",
      "Pochette personnalisée incluse",
      "Jusqu'à 20 chansons par playlist",
      "Service de recherche musical"
    ],
    contents: [
      "Support musical personnalisé",
      "Pochette avec liste des titres",
      "Livret avec anecdotes sur les chansons",
      "Instructions d'utilisation",
      "Certificat d'authenticité"
    ],
    tips: [
      "Demandez à votre grand-parent ses chansons préférées",
      "Incluez des tubes de leur jeunesse (années 40-60)",
      "N'oubliez pas les chansons de danse comme la valse",
      "Ajoutez quelques surprises de votre part",
      "Créez un moment d'écoute en famille"
    ]
  },
  {
    id: 9,
    name: "Apprendre à dessiner",
    description: "Offrez un kit pour apprendre à dessiner, avec un livre tutoriel parfait pour initier les grands-parents à l'art du dessin de manière ludique et progressive — un cadeau qui invite à explorer formes, perspectives et styles, et à cultiver le plaisir de créer à tout âge.",
    detailedDescription: "Découvrez l'art du dessin avec notre kit d'apprentissage complet, conçu spécialement pour les débutants de tous âges. Ce kit comprend un livre tutoriel progressif qui enseigne les techniques fondamentales du dessin en seulement 15 minutes par jour. Apprenez à dessiner des formes simples, puis progressez vers des sujets plus complexes comme les oiseaux, les paysages et les portraits. Cette activité stimule la créativité, améliore la concentration et offre un moment de détente et d'épanouissement personnel. Parfait pour développer une nouvelle passion artistique ou redécouvrir le plaisir de créer.",
    image: "/lovable-uploads/646bff39-4a82-468a-8d15-739dba499ff1.png",
    category: "Créatif",
    price: "Inclus dans l'abonnement",
    duration: "15 minutes par jour",
    difficulty: "Débutant à intermédiaire",
    benefits: [
      "Stimule la créativité et l'imagination",
      "Améliore la coordination œil-main",
      "Favorise la concentration et la patience",
      "Procure un sentiment d'accomplissement",
      "Activité relaxante et méditative"
    ],
    features: [
      "Livre tutoriel progressif de 120 pages",
      "Méthode simple en 15 min par jour",
      "Techniques de base à avancées",
      "Nombreux exemples illustrés",
      "Exercices pratiques guidés",
      "Conseils de professionnels"
    ],
    contents: [
      "1 livre 'Apprendre à tout dessiner'",
      "Crayons de différentes duretés (2H, HB, 2B)",
      "Gomme et estompe",
      "Carnet de croquis A4",
      "Guide des techniques de base"
    ],
    tips: [
      "Commencez par 15 minutes par jour comme recommandé",
      "Pratiquez dans un endroit bien éclairé",
      "N'hésitez pas à recommencer les exercices",
      "Gardez tous vos dessins pour voir vos progrès",
      "Partagez vos créations avec la famille"
    ]
  },
  {
    id: 10,
    name: "Kit d'argile créative",
    description: "Un kit avec pour exprimer sa créativité avec un bloc d'1,5kg d'argile, du papier ponce, de la peinture et du vernis, accompagné de vidéos tutoriels sous-titrées pour créer des objets décoratifs à offrir… Ou à garder.",
    detailedDescription: "Découvrez l'art de la poterie et de la sculpture avec notre kit d'argile créative complet. Cette activité manuelle permet de créer des objets décoratifs uniques tout en travaillant la motricité fine et en stimulant la créativité. Le kit comprend tout le nécessaire pour débuter : argile de qualité, outils de modelage, peintures et vernis pour la finition. Les vidéos tutoriels sous-titrées guident pas à pas dans la réalisation de différents projets : bols, vases, sculptures décoratives, porte-objets personnalisés. Une activité thérapeutique qui procure un grand sentiment d'accomplissement et permet de créer des cadeaux personnalisés pour la famille.",
    image: "/lovable-uploads/631ee35a-8387-4169-8964-8e631ce80717.png",
    category: "Activité manuelle",
    price: "Inclus dans l'abonnement",
    duration: "2-4 heures par projet",
    difficulty: "Débutant à intermédiaire",
    benefits: [
      "Développe la créativité et l'expression artistique",
      "Améliore la motricité fine et la dextérité",
      "Activité thérapeutique et relaxante",
      "Stimule la concentration et la patience",
      "Crée des objets utiles et décoratifs"
    ],
    features: [
      "Bloc d'argile naturelle de 1,5kg",
      "Vidéos tutoriels sous-titrées",
      "Outils de modelage professionnels",
      "Peintures acryliques multi-couleurs",
      "Vernis de finition brillant",
      "Guide des techniques de base"
    ],
    contents: [
      "1 bloc d'argile naturelle (1,5kg)",
      "Outils de modelage (mirette, ébauchoir, lissoir)",
      "Papier ponce grain fin",
      "Set de peintures acryliques (6 couleurs)",
      "Vernis de protection transparent",
      "Accès aux vidéos tutoriels en ligne"
    ],
    tips: [
      "Travaillez sur une surface protégée",
      "Gardez l'argile humide pendant le modelage",
      "Laissez sécher 24h avant de poncer",
      "Appliquez la peinture en fines couches",
      "Terminez toujours par une couche de vernis"
    ]
  },
  {
    id: 11,
    name: "Soirée casino",
    description: "Offrez le frisson du jeu à domicile : un coffret casino comprenant mélangeur de cartes, cartes et jetons, pour transformer la table du salon de vos grands-parents en véritable tapis vert et vivre ensemble des parties animées, pleines de rires et de stratégie.",
    detailedDescription: "Transformez votre salon en véritable casino avec ce coffret complet qui réunit tout le nécessaire pour organiser des soirées jeux mémorables en famille. Ce kit casino premium comprend un mélangeur de cartes automatique, des jeux de cartes professionnels, des jetons colorés et un livret de règles des jeux les plus populaires. Parfait pour rassembler la famille autour de parties de poker, blackjack, belote ou tout autre jeu de cartes. Cette activité favorise les interactions sociales, stimule la réflexion stratégique et crée des moments de complicité intergénérationnels. Une excellente façon de passer des soirées divertissantes tout en gardant l'esprit vif et alerte.",
    image: "/lovable-uploads/52d07fe8-ed5e-4a92-931f-587e434109a4.png",
    category: "Loisir",
    price: "Inclus dans l'abonnement",
    duration: "1-3 heures par partie",
    difficulty: "Tous niveaux",
    benefits: [
      "Stimule les fonctions cognitives et la mémoire",
      "Encourage les interactions sociales",
      "Développe la stratégie et la logique",
      "Crée des moments de convivialité familiale",
      "Maintient l'esprit alerte et concentré"
    ],
    features: [
      "Mélangeur de cartes automatique",
      "Jeux de cartes professionnels",
      "Jetons de casino multicolores",
      "Livret de règles détaillé",
      "Tapis de jeu anti-dérapant",
      "Bouton dealer inclus"
    ],
    contents: [
      "1 mélangeur de cartes automatique",
      "2 jeux de cartes professionnels",
      "150 jetons colorés (5 couleurs)",
      "1 tapis de jeu pliable",
      "1 bouton dealer",
      "Livret des règles (10 jeux différents)"
    ],
      tips: [
        "Commencez par des jeux simples comme la belote",
        "Établissez des règles claires avant de commencer",
        "Jouez avec de petites mises symboliques",
        "Alternez les jeux pour maintenir l'intérêt",
        "Préparez des rafraîchissements pour l'ambiance"
      ]
    },
    {
      id: 16,
      name: "Initiation au crochet",
      description: "Initiez vos grands-parents au crochet avec un kit complet pour apprendre a confectionner une peluche parfait pour les débutants comme pour les confirmés.",
      image: "/lovable-uploads/1d7e235f-db21-4097-b873-ad6036fae15e.png",
      category: "Créatif",
      price: "Inclus dans l'abonnement",
      variants: [
        { id: "crochet-1", label: "Koala", value: "koala" },
        { id: "crochet-2", label: "Teckel", value: "teckel" },
        { id: "crochet-3", label: "Lion", value: "lion" },
        { id: "crochet-4", label: "Lapin", value: "lapin" },
        { id: "crochet-5", label: "Renard", value: "renard" },
        { id: "crochet-6", label: "Castor", value: "castor" },
        { id: "crochet-7", label: "Phoque", value: "phoque" },
        { id: "crochet-8", label: "Chat", value: "chat" }
      ],
      detailedDescription: "Découvrez l'art du crochet avec ce kit d'initiation complet ! Parfait pour les débutants, ce kit permet de créer une adorable peluche tout en apprenant les techniques de base du crochet. Choisissez parmi 8 modèles d'animaux mignons, chacun conçu pour progresser dans l'apprentissage. Une activité relaxante et créative qui stimule la dextérité et procure un grand sentiment d'accomplissement.",
      features: [
        "Kit complet pour débutants",
        "Instructions détaillées étape par étape",
        "Choix de 8 peluches différentes",
        "Matériel de qualité inclus",
        "Techniques de base expliquées",
        "Résultat garanti"
      ],
      contents: [
        "Pelotes de laine de qualité",
        "Crochets adaptés aux débutants",
        "Patron détaillé avec photos",
        "Aiguille à laine",
        "Rembourrage synthétique",
        "Guide des points de base"
      ],
      duration: "4-6 heures",
      difficulty: "Débutant",
      benefits: [
        "Développe la dextérité",
        "Activité relaxante et méditative",
        "Stimule la créativité",
        "Procure un sentiment d'accomplissement"
      ],
      tips: [
        "Commencez par le modèle Koala, le plus simple",
        "Prenez votre temps pour les premiers points",
        "N'hésitez pas à défaire et recommencer",
        "Gardez une tension régulière sur le fil"
      ]
    },
    {
      id: 17,
      name: "Initiation au sport doux",
      description: "Aidez vos grands-parents à garder la forme en offrant un coffret sportif, composé de poids léger, d'élastique, et d'une balle pour stimuler la mobilité et les réflexes en douceur. Ce kit contient également des vidéos proposant plusieurs séances de sport adaptées aux niveaux de chacun. A faire seul chez soi… ou à plusieurs !",
      image: "/lovable-uploads/91f9d140-80bf-45df-b8c6-dbae6cac3142.png",
      category: "Bien-être",
      price: "Inclus dans l'abonnement",
      variants: [
        { id: "sport-1", label: "Haltères 0.5kg", value: "halteres-0.5kg" },
        { id: "sport-2", label: "Haltères 1kg", value: "halteres-1kg" }
      ],
      detailedDescription: "Découvrez le plaisir de l'activité physique adaptée avec ce kit de sport doux conçu spécialement pour les seniors. Ce coffret complet propose des exercices progressifs pour maintenir la forme, améliorer l'équilibre et renforcer les muscles en douceur. Grâce aux vidéos incluses, profitez de séances guidées adaptées à votre niveau, que vous soyez débutant ou déjà actif. Une excellente façon de rester en forme à son rythme, seul ou en famille.",
      features: [
        "Kit complet adapté aux seniors",
        "Exercices progressifs et sécurisés",
        "Vidéos de séances guidées",
        "Matériel léger et ergonomique",
        "Activité solo ou en groupe",
        "Renforcement musculaire en douceur"
      ],
      contents: [
        "Paire d'haltères légers (0.5kg ou 1kg au choix)",
        "Élastique de résistance avec poignées",
        "Balle de stimulation pour réflexes",
        "Tapis d'exercice antidérapant",
        "Accès aux vidéos d'entraînement",
        "Guide des exercices illustré"
      ],
      duration: "20-30 minutes par séance",
      difficulty: "Débutant à intermédiaire",
      benefits: [
        "Maintient la force musculaire",
        "Améliore l'équilibre et la coordination",
        "Stimule les réflexes et la mobilité",
        "Favorise le bien-être général",
        "Activité sociale si pratiquée en groupe"
      ],
      tips: [
        "Commencez par des séances courtes de 15 minutes",
        "Échauffez-vous toujours avant les exercices",
        "N'oubliez pas de boire de l'eau avant et après votre séance ! :)",
        "Respectez votre rythme et vos limites",
        "Pratiquez régulièrement pour de meilleurs résultats"
      ]
    },
    {
      id: 18,
      name: "Kit de tricot",
      description: "Offrez un kit de tricot avec un guide complet pour expliquer toutes les bases, et laissez vos grands-parents fabriquer un vêtement unique.",
      image: "/lovable-uploads/b391ae0b-4d79-4223-99f0-bcea421a8fb3.png",
      category: "Créativité",
      price: "Inclus dans l'abonnement",
      variants: [
        { id: "tricot-1", label: "Bleu foncé & turquoise", value: "bleu-turquoise" },
        { id: "tricot-2", label: "Pastel", value: "pastel" },
        { id: "tricot-3", label: "Marron & blanc", value: "marron-blanc" },
        { id: "tricot-4", label: "Multicolor", value: "multicolor" }
      ],
      detailedDescription: "Redécouvrez le plaisir du tricot avec ce kit complet conçu pour tous les niveaux. Que vous soyez débutant ou que vous souhaitiez vous remettre au tricot, ce coffret contient tout le nécessaire pour créer un vêtement unique et personnalisé. Grâce au guide détaillé inclus, apprenez les techniques de base et progressez à votre rythme pour réaliser des créations dont vous serez fier.",
      features: [
        "Kit complet pour débutants et confirmés",
        "Guide illustré avec techniques de base",
        "Laines de qualité en plusieurs coloris",
        "Aiguilles adaptées à tous projets",
        "Patrons de vêtements inclus",
        "Activité relaxante et créative"
      ],
      contents: [
        "Pelotes de laine (coloris au choix)",
        "Jeu d'aiguilles à tricoter de différentes tailles",
        "Guide complet des techniques de tricot",
        "Patrons de vêtements avec instructions",
        "Marqueurs de mailles et compteur de rangs",
        "Aiguille à laine pour les finitions"
      ],
      duration: "Plusieurs semaines selon le projet",
      difficulty: "Débutant à avancé",
      benefits: [
        "Développe la dextérité et la concentration",
        "Activité relaxante et anti-stress",
        "Crée des objets utiles et personnalisés",
        "Stimule la créativité",
        "Procure un sentiment d'accomplissement"
      ],
      tips: [
        "Commencez par des projets simples comme une écharpe",
        "Gardez une tension régulière sur le fil",
        "N'hésitez pas à défaire et recommencer si nécessaire",
        "Choisissez un endroit bien éclairé pour tricoter",
        "Prenez des pauses régulières pour reposer vos mains"
      ]
    },
    {
      id: 19,
      name: "Moment de bien-être",
      description: "Offrez un temps pour soi à votre seniors en leur envoyant un kit bien-être composé de deux masques pour le visage MISSHA, d'un gommage pour le corps, d'un vernis, de chaussettes polaires, d'une bougie parfumée et de sachets de tisane bien-être pour prendre soin de soi dans une ambiance cocooning.",
      image: "/lovable-uploads/9a2e9595-b7f6-40ad-84df-d94ad6b4516d.png",
      category: "Bien-être",
      price: "Inclus dans l'abonnement",
      variants: [],
      detailedDescription: "Transformez la maison en véritable spa avec ce kit bien-être complet. Conçu pour offrir un moment de détente et de relaxation, ce coffret contient tout le nécessaire pour une séance de soins personnels réconfortante. Des produits de qualité MISSHA aux accessoires cocooning, chaque élément a été sélectionné pour créer une expérience de bien-être complète et apaisante.",
      features: [
        "Kit complet pour une séance spa à domicile",
        "Produits MISSHA de qualité professionnelle",
        "Ambiance cocooning avec bougie parfumée",
        "Moment de détente et de relaxation",
        "Accessoires confort inclus",
        "Rituel bien-être complet"
      ],
      contents: [
        "2 masques pour le visage MISSHA (Rice et Red Ginseng)",
        "Gommage pour le corps hydratant",
        "Vernis à ongles tendance",
        "Chaussettes polaires ultra-douces",
        "Bougie parfumée pour l'ambiance",
        "Sachets de tisane bien-être bio"
      ],
      duration: "2-3 heures pour une séance complète",
      difficulty: "Accessible à tous",
      benefits: [
        "Améliore l'hydratation et l'éclat de la peau",
        "Procure détente et relaxation profonde",
        "Stimule la confiance en soi",
        "Réduit le stress et les tensions",
        "Crée un moment de plaisir personnel"
      ],
      tips: [
        "Préparez un environnement calme et relaxant",
        "Commencez par une tisane pour vous détendre",
        "Allumez la bougie 30 minutes avant de commencer",
        "Appliquez le masque sur peau propre et sèche",
        "Terminez par le gommage sous la douche pour une peau douce"
      ]
    },
    {
      id: 20,
      name: "Kit initiation aux origami",
      description: "Offrez une initiation complète pour apprendre à réaliser ses propres origami. Plus de 50 modèles expliqué pas à pas pour apprendre cet art ancestral !",
      image: "/lovable-uploads/842b3f37-e438-4ee2-b9c7-4fdf1f85b833.png",
      category: "Créatif",
      price: "Inclus dans l'abonnement",
      variants: [],
      detailedDescription: "Découvrez l'art millénaire de l'origami avec ce kit complet d'initiation. Grâce à plus de 50 modèles soigneusement sélectionnés et expliqués étape par étape, vous apprendrez progressivement les techniques fondamentales de cet art japonais fascinant. Du pliage le plus simple aux créations plus complexes, ce guide vous accompagnera dans votre apprentissage avec patience et précision.",
      features: [
        "Plus de 50 modèles d'origami détaillés",
        "Explications pas à pas avec illustrations",
        "Progression du niveau débutant à avancé",
        "Papiers origami de qualité inclus",
        "Techniques traditionnelles japonaises",
        "Art ancestral relaxant et créatif"
      ],
      contents: [
        "Livre d'initiation avec 50+ modèles",
        "Assortiment de papiers origami colorés",
        "Guide des plis de base illustré",
        "Papiers à motifs traditionnels japonais",
        "Instructions détaillées étape par étape",
        "Histoire et philosophie de l'origami"
      ],
      duration: "Activité continue, 30 min par modèle",
      difficulty: "Débutant à avancé",
      benefits: [
        "Développe la concentration et la patience",
        "Améliore la dextérité et la précision",
        "Stimule la créativité et l'imagination",
        "Procure un sentiment d'accomplissement",
        "Activité relaxante et méditative"
      ],
      tips: [
        "Commencez par les modèles les plus simples",
        "Travaillez sur une surface plane et propre",
        "Respectez bien les plis de base avant de continuer",
        "Prenez votre temps, la précision est importante",
        "N'hésitez pas à recommencer si le pli n'est pas net"
      ]
    },
    {
      id: 21,
      name: "Créer son carnet de recettes familiales",
      description: "Offrez leurs de quoi créer leur propre livre de cuisine personnalisé et conservez les recettes familiales, avec un carnet structuré pour y ajouter les ingrédients, les étapes, le temps de préparation et tout autre détail qui rendent la cuisine de nos grands parents si spéciale.",
      image: "/lovable-uploads/e4f2cfc0-6bbf-494c-bdeb-c68f654dbda1.png",
      category: "Mémoriel",
      price: "Inclus dans l'abonnement",
      variants: [],
      detailedDescription: "Préservez et transmettez les trésors culinaires de votre famille avec ce magnifique carnet de recettes personnalisé. Conçu spécialement pour capturer l'essence de la cuisine familiale, ce carnet structuré permet de documenter non seulement les ingrédients et les étapes, mais aussi les petits secrets, anecdotes et souvenirs qui rendent chaque recette unique et précieuse.",
      features: [
        "Carnet de recettes structuré et élégant",
        "Pages dédiées aux souvenirs culinaires",
        "Sections pour tous types de plats",
        "Espace pour photos et anecdotes",
        "Reliure solide pour une conservation durable",
        "Design vintage et authentique"
      ],
      contents: [
        "Carnet de recettes relié 125 pages",
        "Pages structurées pour ingrédients et étapes",
        "Sections dédiées aux temps de préparation",
        "Espaces pour notes personnelles et anecdotes",
        "Pages pour coller des photos de plats",
        "Index alphabétique pour retrouver facilement"
      ],
      duration: "Projet à vie, remplissage progressif",
      difficulty: "Accessible à tous",
      benefits: [
        "Préserve les traditions culinaires familiales",
        "Crée un héritage tangible pour les générations futures",
        "Stimule la mémoire et les souvenirs",
        "Encourage le partage d'histoires familiales",
        "Valorise l'expertise culinaire des aînés"
      ],
      tips: [
        "Commencez par vos recettes les plus importantes",
        "Notez les petits secrets qui font la différence",
        "Ajoutez des anecdotes liées à chaque recette",
        "Incluez l'origine de la recette (grand-mère, région...)",
        "Prenez des photos des plats terminés pour illustrer"
      ]
    }
  ];

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedActivities, setSelectedActivities] = useState<number[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<string>("");
  const [activityVariants, setActivityVariants] = useState<Record<number, string>>({});
  const [customPhotoUrl, setCustomPhotoUrl] = useState<string>("");

  const activity = activities.find(a => a.id === parseInt(id || '0'));

  // Définir les variantes et images pour le kit de plantes (compatibilité avec l'ancien système)
  const plantVariants = [
    { label: "Kit herbe aromatique", value: "kit herbe aromatique", image: "/lovable-uploads/3684dd22-b0f2-43ff-8143-89f55281de80.png" },
    { label: "Kit Cactus", value: "kit cactus", image: "/lovable-uploads/fc64caef-7b17-448c-9896-d4acf73d4d92.png" },
    { label: "Kit Bonzaï", value: "kit bonzaï", image: "/lovable-uploads/502470bc-6927-49dc-84bc-c6b30cb4f384.png" }
  ];

  // Définir les variantes pour l'activité musicale (compatibilité avec l'ancien système)
  const musicVariants = [
    { label: "CD", value: "cd", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" },
    { label: "Vinyle", value: "vinyle", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" },
    { label: "Clé USB", value: "usb", image: "/lovable-uploads/7e9193e9-432c-48b1-a389-22ba17be6e02.png" }
  ];

  // État pour la playlist personnalisée
  const [playlist, setPlaylist] = useState<string[]>(['']);
  
  const addSongToPlaylist = () => {
    setPlaylist([...playlist, '']);
  };
  
  const updateSong = (index: number, value: string) => {
    const newPlaylist = [...playlist];
    newPlaylist[index] = value;
    setPlaylist(newPlaylist);
  };
  
  const removeSong = (index: number) => {
    if (playlist.length > 1) {
      setPlaylist(playlist.filter((_, i) => i !== index));
    }
  };

  // Image actuelle selon la variante sélectionnée
  const getCurrentImage = () => {
    if (activity?.id === 2 && selectedVariant) {
      const variant = plantVariants.find(v => v.value === selectedVariant);
      return variant?.image || activity.image;
    }
    if (activity?.id === 8 && selectedVariant) {
      const variant = musicVariants.find(v => v.value === selectedVariant);
      return variant?.image || activity.image;
    }
    return activity?.image || '';
  };

  useEffect(() => {
    // Charger les données sauvegardées au démarrage
    const orderData = getOrderData();
    if (orderData && orderData.orderType === 'custom') {
      setSelectedActivities(orderData.selectedActivities.map(a => a.id));
      if (orderData.activityVariants) {
        setActivityVariants(orderData.activityVariants);
        // Charger le variant sélectionné pour cette activité
        if (activity && orderData.activityVariants[activity.id]) {
          setSelectedVariant(orderData.activityVariants[activity.id]);
        }
      }
      // Charger l'URL de la photo personnalisée pour le puzzle
      if (activity?.id === 1) {
        const savedActivity = orderData.selectedActivities.find(a => a.id === 1);
        if (savedActivity?.customPhotoUrl) {
          setCustomPhotoUrl(savedActivity.customPhotoUrl);
        }
      }
    }
  }, [activity]);

  if (!activity) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Activité non trouvée</h1>
          <Button onClick={() => navigate('/activites')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux activités
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const isSelected = selectedActivities.includes(activity.id);

  const toggleActivity = () => {
    // Vérifier qu'un variant est sélectionné pour les activités qui en ont
    if (activity?.variants && activity.variants.length > 0 && !selectedVariant) {
      return; // Ne pas permettre l'ajout sans variant
    }

    // Pour le puzzle personnalisé, vérifier qu'une photo est uploadée
    if (activity?.id === 1 && !customPhotoUrl) {
      return; // Ne pas permettre l'ajout sans photo
    }

    const newSelected = isSelected
      ? selectedActivities.filter(id => id !== activity.id)
      : [...selectedActivities, activity.id];
    
    setSelectedActivities(newSelected);
    
    // Mettre à jour les variants
    const newVariants = { ...activityVariants };
    if (activity && selectedVariant) {
      newVariants[activity.id] = selectedVariant;
    }
    setActivityVariants(newVariants);
    
    // Sauvegarder les activités sélectionnées
    const selectedActivityData: SelectedActivity[] = newSelected.map(id => {
      const act = activities.find(a => a.id === id);
      if (!act) return null;
      
      let activityName = act.name;
      let selectedActivityVariant = undefined;
      
      // Ajouter le variant au nom si une variante est sélectionnée
      if (act.variants && act.variants.length > 0 && newVariants[id]) {
        const variant = act.variants.find(v => v.value === newVariants[id]);
        activityName = `${act.name} - ${variant?.label || newVariants[id]}`;
        selectedActivityVariant = newVariants[id];
      }
      
      return {
        id: act.id,
        name: activityName,
        description: act.description,
        image: act.image,
        category: act.category,
        variant: selectedActivityVariant,
        customPhotoUrl: act.id === 1 ? customPhotoUrl : undefined
      };
    }).filter(Boolean) as SelectedActivity[];

    saveOrderData({
      orderType: 'custom',
      selectedActivities: selectedActivityData,
      activityVariants: newVariants,
      frequency: 3
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Créatif": return "bg-primary text-primary-foreground";
      case "Mémoriel": return "bg-warm-brown text-white";
      case "Loisir": return "bg-primary-light text-white";
      case "Bien-être": return "bg-accent-foreground text-white";
      case "Activité manuelle": return "bg-secondary text-secondary-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Bouton retour */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/activites')}
          className="mb-6 text-primary hover:text-primary/80"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux activités
        </Button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image du produit */}
          <div className="space-y-4">
            <div className="aspect-square bg-muted rounded-lg overflow-hidden relative">
              <img 
                src={getCurrentImage()} 
                alt={activity.name}
                className="w-full h-full object-cover transition-all duration-300"
              />
              <Badge 
                className={`absolute top-4 left-4 ${getCategoryColor(activity.category)}`}
              >
                {activity.category}
              </Badge>
            </div>
          </div>

          {/* Informations du produit */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">
                {activity.name}
              </h1>
              <div className="text-2xl font-bold text-primary mb-4">
                {activity.price}
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {activity.detailedDescription}
              </p>
            </div>

            {/* Sélection générique de variantes pour toutes les activités qui en ont */}
            {activity.variants && activity.variants.length > 0 && (
              <Card className="p-6 mb-6 bg-muted/30 border-muted">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Choisissez votre option
                </h3>
                <Select 
                  value={selectedVariant || ""} 
                  onValueChange={setSelectedVariant}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choisir une option" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border shadow-lg z-50">
                    {activity.variants.map((variant) => (
                      <SelectItem key={variant.id} value={variant.value}>
                        {variant.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Card>
            )}

            {/* Sélection de format pour l'activité musicale - garde la fonctionnalité playlist */}
            {activity.id === 8 && selectedVariant && (
              <Card className="p-6 mb-6 bg-muted/30 border-muted">
                <h4 className="text-md font-semibold text-foreground mb-4">
                  Créez votre playlist personnalisée
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Ajoutez les chansons qui rappelleront de beaux souvenirs à votre grand-parent
                </p>
                <div className="space-y-3">
                  {playlist.map((song, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder={`Chanson ${index + 1} (ex: "La Vie en Rose - Édith Piaf")`}
                        value={song}
                        onChange={(e) => updateSong(index, e.target.value)}
                        className="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      {playlist.length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeSong(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addSongToPlaylist}
                    className="w-full"
                  >
                    + Ajouter une chanson
                  </Button>
                </div>
              </Card>
            )}

            {/* Upload de photo pour le puzzle personnalisé */}
            {activity.id === 1 && (
              <div className="mb-6">
                <PhotoUpload
                  onPhotoUploaded={setCustomPhotoUrl}
                  currentPhotoUrl={customPhotoUrl}
                  onPhotoRemoved={() => setCustomPhotoUrl("")}
                />
              </div>
            )}

            {/* Bouton d'action */}
            <Card className="p-6 bg-primary/5 border-primary/20">
              <Button 
                onClick={toggleActivity}
                size="lg"
                disabled={
                  (activity.variants && activity.variants.length > 0 && !selectedVariant) ||
                  (activity.id === 1 && !customPhotoUrl)
                }
                className={`w-full mb-4 ${
                  isSelected 
                    ? 'bg-primary hover:bg-primary/90' 
                    : 'bg-primary hover:bg-primary/90'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSelected ? (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Activité sélectionnée
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5 mr-2" />
                    {activity.id === 1 && !customPhotoUrl 
                      ? "Vous devez upload une photo pour cette activité"
                      : (activity.variants && activity.variants.length > 0 && !selectedVariant) 
                        ? "Choisissez d'abord une option" 
                        : "Ajouter à ma liste"}
                  </>
                )}
              </Button>
              
              {selectedActivities.length > 0 && (
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="w-full border-primary text-primary hover:bg-primary/10"
                  onClick={() => navigate('/activites')}
                >
                  Sélectionner plus d'activités ({selectedActivities.length})
                </Button>
              )}
            </Card>

            {/* Avantages */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4">
                <Package className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Livraison gratuite</p>
              </div>
              <div className="text-center p-4">
                <Shield className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Sans engagement</p>
              </div>
              <div className="text-center p-4">
                <Truck className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Envoi mensuel</p>
              </div>
            </div>
          </div>
        </div>

        {/* Détails du contenu */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          {/* Durée et difficulté */}
          {(activity.duration || activity.difficulty) && (
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-foreground mb-4">
                Informations pratiques
              </h3>
              <div className="space-y-3">
                {activity.duration && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Durée estimée</p>
                    <p className="text-foreground">{activity.duration}</p>
                  </div>
                )}
                {activity.difficulty && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Niveau</p>
                    <Badge variant="outline" className="text-primary border-primary">
                      {activity.difficulty}
                    </Badge>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Caractéristiques principales */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              Caractéristiques principales
            </h3>
            <ul className="space-y-2">
              {activity.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Contenu du kit */}
          <Card className="p-6">
            <h3 className="text-xl font-semibold text-foreground mb-4">
              Contenu du kit
            </h3>
            <ul className="space-y-2">
              {activity.contents.map((content, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Package className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{content}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Bienfaits et conseils */}
        {(activity.benefits || activity.tips) && (
          <div className="grid md:grid-cols-2 gap-8 mt-8">
            {/* Bienfaits */}
            {activity.benefits && (
              <Card className="p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Bienfaits de cette activité
                </h3>
                <ul className="space-y-3">
                  {activity.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0"></div>
                      <span className="text-muted-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Conseils */}
            {activity.tips && (
              <Card className="p-6 bg-gradient-to-br from-secondary/5 to-accent/5 border-secondary/20">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  Conseils pour bien commencer
                </h3>
                <ul className="space-y-3">
                  {activity.tips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-secondary/20 text-secondary font-semibold text-sm flex items-center justify-center mt-0.5 flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-muted-foreground">{tip}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        {/* CTA final */}
        <Card className="p-8 mt-16 text-center bg-gradient-soft">
          <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">
            Prêt à faire plaisir à votre grand-parent ?
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Ajoutez cette activité à votre sélection et créez une box personnalisée qui apportera joie et stimulation à votre proche.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={toggleActivity}
              size="lg"
              disabled={activity.variants && activity.variants.length > 0 && !selectedVariant}
              className="bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSelected ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Activité ajoutée
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 mr-2" />
                  {(activity.variants && activity.variants.length > 0 && !selectedVariant) ? "Choisissez d'abord une option" : "Ajouter cette activité"}
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => navigate('/activites')}
              className="border-primary text-primary hover:bg-primary/10"
            >
              Voir d'autres activités
            </Button>
          </div>
        </Card>
      </main>
      
      <Footer />
    </div>
  );
};

export default ActivityDetail;